import { InventoryItemOutcome } from "@redotech/crdb-enums/inventory/inventory-item-outcome";
import * as z from "zod";

export const TRANSFER_BLOCKED = "TRANSFER_BLOCKED";

export const TransferBlockedCode = {
  notRestockEligible: "not_restock_eligible",
  sortDispositionMismatch: "sort_disposition_mismatch",
  sortingToteDestination: "sorting_tote_destination",
  ambiguousPlacement: "ambiguous_placement",
  alreadyReceived: "already_received",
  consolidationMerchantMix: "consolidation_merchant_mix",
  consolidationBinMerchantMismatch: "consolidation_bin_merchant_mismatch",
  consolidationBinCriteriaMismatch: "consolidation_bin_criteria_mismatch",
  consolidationBinItemRequired: "consolidation_bin_item_required",
  externalItem: "external_item",
  productDeleted: "product_deleted",
} as const;
export type TransferBlockedCode =
  (typeof TransferBlockedCode)[keyof typeof TransferBlockedCode];

export const TransferBlockedPayloadSchema = z.object({
  code: z.enum([
    TransferBlockedCode.notRestockEligible,
    TransferBlockedCode.sortDispositionMismatch,
    TransferBlockedCode.sortingToteDestination,
    TransferBlockedCode.ambiguousPlacement,
    TransferBlockedCode.alreadyReceived,
    TransferBlockedCode.consolidationMerchantMix,
    TransferBlockedCode.consolidationBinMerchantMismatch,
    TransferBlockedCode.consolidationBinCriteriaMismatch,
    TransferBlockedCode.consolidationBinItemRequired,
    TransferBlockedCode.externalItem,
    TransferBlockedCode.productDeleted,
  ]),
  grade: z.string().nullable(),
  disposition: z.string().nullable(),
  binName: z.string(),
  sortDisposition: z.string().nullish(),
  outcome: z.enum(InventoryItemOutcome).nullish(),
  sortDestinationName: z.string().nullish(),
  inventoryItemId: z.string().nullish(),
  locationNames: z.array(z.string()).nullish(),
  merchantName: z.string().nullish(),
  // Declared here rather than reusing the bin's criteria schema: that module
  // imports these codes, and the payload is a wire contract of its own.
  criteria: z
    .object({
      skus: z.array(z.string()).optional(),
      grades: z.array(z.string()).optional(),
      fulfillable: z.boolean().optional(),
    })
    .nullish(),
});
export type TransferBlockedPayload = z.infer<
  typeof TransferBlockedPayloadSchema
>;

const OVERRIDABLE_CODES = new Set<TransferBlockedCode>([
  TransferBlockedCode.notRestockEligible,
  TransferBlockedCode.sortDispositionMismatch,
  TransferBlockedCode.alreadyReceived,
  TransferBlockedCode.consolidationMerchantMix,
  TransferBlockedCode.consolidationBinMerchantMismatch,
  TransferBlockedCode.consolidationBinCriteriaMismatch,
]);

export function isTransferBlockOverridable(code: TransferBlockedCode): boolean {
  return OVERRIDABLE_CODES.has(code);
}

export function parseTransferBlocked(
  error: unknown,
): TransferBlockedPayload | null {
  if (!error || typeof error !== "object") {
    return null;
  }
  const { code, message } = error as { code?: unknown; message?: unknown };
  if (code !== TRANSFER_BLOCKED || typeof message !== "string") {
    return null;
  }
  return parseTransferBlockedText(message);
}

export function parseTransferBlockedText(
  text: string,
): TransferBlockedPayload | null {
  try {
    return TransferBlockedPayloadSchema.parse(JSON.parse(text));
  } catch {
    return null;
  }
}
